import classNames from "classnames";
import { Button } from "antd";
import { capitalize, random } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMemo } from "react";

import Spinner from "@/components/Spinner";
import CardUserProfile from "@/components/v2/CardUserProfile/index";
import ErrorEmptyV2 from "@/components/v2/ErrorEmpty";
import { Circle } from "@/graphql/graphql";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useListCircles } from "@/hooks/useCircles";
import useAuthStore from "@/store/useAuthStore";
import { PAGE_URL } from "@/utils/constants";

type RecommendedCircleWidgetProps = {
  wrapperClassName?: string;
};

export default function RecommendedCircleWidget({
  wrapperClassName = "",
}: RecommendedCircleWidgetProps) {
  const router = useRouter();
  const authUser = useAuthStore.useUser();
  const { data, isLoading } = useListCircles();

  const dataRecommedCircles: Circle[] = useMemo(() => {
    return (
      data?.items?.filter((item: any) => {
        return !item.members?.items?.some(
          (row: any) =>
            row.circleUserUserId === authUser?.profile?.user_id &&
            row.status === "APPROVED"
        );
      }) || []
    );
  }, [data, authUser?.profile?.user_id]);

  const randomIndex = useMemo(() => {
    if (dataRecommedCircles.length > 0) {
      return random(0, dataRecommedCircles.length - 1);
    }
    return 0;
  }, [dataRecommedCircles]);

  const recommendedCircle = dataRecommedCircles?.[randomIndex];

  const { track } = useAnalytics();

  const onClickViewMore = () => {
    track("View more recommended circle", "Click", `Page ${router.pathname}`);
    router.push({
      pathname: PAGE_URL.mycalendar,
    });
  };

  return (
    <div
      className={`bg-card relative px-4 py-8 w-full flex flex-col h-full justify-center ${wrapperClassName}`}
    >
      {!isLoading && recommendedCircle && (
        <div className="top-4 right-6 flex justify-end absolute z-10">
          <Button type="text" style={{ padding: 0 }} onClick={onClickViewMore}>
            <span className="text-yellow">View More Circles</span>
          </Button>
        </div>
      )}
      <div
        className={classNames(
          "flex justify-center items-end lg:items-center text-white lg:h-[136px]",
          !recommendedCircle?.id ? "mt-0" : "mt-7 xl:mt-0"
        )}
      >
        {isLoading ? (
          <Spinner size="md" />
        ) : recommendedCircle ? (
          <Link
            href={`${PAGE_URL.circles}/${recommendedCircle.id}`}
            shallow
            onClick={() => {
              track(
                "Click recommended circle",
                "Click",
                `Circle ID: ${recommendedCircle.id}`
              );
            }}
          >
            <CardUserProfile
              avatarSize="sm"
              variant="vertical"
              containerClassName="!pt-0"
              item={{
                firstName: recommendedCircle.name || "",
                lastName: "",
                avatarUrl: recommendedCircle.imageUrl || "",
                ...(recommendedCircle || {}) as any,
              }}
              customType={`${capitalize(recommendedCircle?.privacy)} Circle`}
              findCGCTX="CIRCLE"
              hideMutual
              hideJobType
              squareAvatar
              type="circle"
              isNameCapitalized={false}
              disableHref={true}
            />
          </Link>
        ) : (
          <ErrorEmptyV2 isFullWidth which="dashboard-recommendedCircle" />
        )}
      </div>
    </div>
  );
}
